import React, { useState } from 'react';
import { 
  Box, Container, Typography, Button, 
  Accordion, AccordionSummary, AccordionDetails, Stack 
} from '@mui/material';
import { 
  ExpandMore, HelpOutline, Email, QuestionAnswer, 
  LightbulbCircle 
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

export default function FAQ() {
  const primaryGradient = 'linear-gradient(135deg, #FF6A00 0%, #EE0979 100%)';
  const darkBg = '#070B14';
  const lightBg = '#f8fafc';

  // Accordion State Handle करने के लिए (सिर्फ एक बार में एक खुलेगा)
  const [expanded, setExpanded] = useState('panel0');

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // FAQ Data Array
  const faqs = [
    {
      q: "What is PracharApp?",
      a: "PracharApp is a digital platform designed specifically for political leaders, party members, and local representatives in India to create professional-quality posters and videos quickly and easily. Our platform offers thousands of templates tailored for political campaigns, events, and announcements."
    },
    {
      q: "How do I create a poster?",
      a: "Creating a poster is simple: 1) Choose from our extensive template library. 2) Customize the text, images, and colors to match your campaign. 3) Download your high-quality poster and share it on social media or print it for offline use."
    },
    {
      q: "What languages are supported?",
      a: "PracharApp supports multiple Indian languages including Hindi, English, Tamil, Telugu, Bengali, Marathi, Gujarati, Kannada, Malayalam, Punjabi, and many more regional languages to cater to diverse political campaigns across India."
    },
    {
      q: "Is there a mobile app available?",
      a: "Yes! PracharApp is available as a mobile application for both Android and iOS devices. You can download it from the Google Play Store or Apple App Store. The mobile app offers the same great features, allowing you to create posters on the go."
    },
    {
      q: "What are the pricing plans?",
      a: "We offer flexible pricing plans to suit different needs: a Free plan with basic templates, a Premium plan with advanced features and unlimited downloads, and an Enterprise plan for large political organizations. Visit our Pricing page for detailed information."
    },
    {
      q: "Can I upload my own images?",
      a: "Absolutely! You can upload your own photos, logos, and images to personalize your posters. We support common image formats like JPG, PNG, and GIF. Make sure your images are high-resolution for the best quality output."
    },
    {
      q: "What output formats are available?",
      a: "You can download your posters in various formats including high-resolution PNG, JPG, and PDF. We also offer different size options optimized for social media platforms (WhatsApp, Facebook, Instagram), print materials, and digital displays."
    },
    {
      q: "Is my data secure?",
      a: "Yes, we take data security very seriously. All your personal information and created content are encrypted and stored securely. We follow industry-standard security practices. Read our Privacy Policy for more details."
    },
    {
      q: "How can I get support?",
      a: "We offer multiple support channels: email support at support@pracharapp.com, live WhatsApp chat during business hours, and comprehensive help documentation. Premium users get priority support with faster response times."
    },
    {
      q: "Can I cancel my subscription anytime?",
      a: "Yes, you can cancel your subscription at any time from your account settings. There are no cancellation fees, and you'll continue to have access to premium features until the end of your current billing period."
    }
  ];

  return (
    <Box sx={{ bgcolor: lightBg, minHeight: '100vh', pb: 8 }}>
      
      {/* 🌟 PAGE HEADER 🌟 */}
      <Box sx={{ position: 'relative', pt: { xs: 10, md: 14 }, pb: { xs: 6, md: 8 }, bgcolor: darkBg, overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '50%', height: '2px', background: primaryGradient, boxShadow: '0 0 20px 5px rgba(255, 81, 47, 0.4)' }} />
        
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Box sx={{ width: 60, height: 60, mx: 'auto', mb: 2, borderRadius: '16px', background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.1)' }}>
            <QuestionAnswer sx={{ fontSize: 32, color: '#FF6A00' }} />
          </Box>
          <Typography variant="h3" fontWeight="900" color="white" sx={{ mb: 1.5, letterSpacing: '-0.5px', fontSize: { xs: '2.2rem', md: '3.2rem' } }}>
            Frequently Asked Questions
          </Typography>
          <Typography variant="subtitle1" color="#94A3B8" fontWeight="500">
            Find answers to common questions about PracharApp
          </Typography>
        </Container>
      </Box>

      {/* 🌟 FAQ ACCORDION SECTION 🌟 */}
      <Container maxWidth="md" sx={{ mt: { xs: -3, md: -5 }, position: 'relative', zIndex: 2 }}>
        <Box sx={{ mb: 6 }}>
          {faqs.map((faq, index) => {
            const isExpanded = expanded === `panel${index}`;
            return (
              <Accordion 
                key={index}
                expanded={isExpanded} 
                onChange={handleChange(`panel${index}`)}
                disableGutters // Removes default weird margins
                elevation={0}
                sx={{
                  mb: 2, // कार्ड्स के बीच गैप
                  borderRadius: '12px !important', // Force border radius
                  bgcolor: 'white',
                  border: isExpanded ? '1px solid #FF6A00' : '1px solid rgba(0,0,0,0.05)',
                  boxShadow: isExpanded ? '0 8px 25px rgba(255,106,0,0.12)' : '0 2px 10px rgba(0,0,0,0.02)',
                  transition: 'all 0.3s ease',
                  '&:before': { display: 'none' }, // Default MUI divider line हटाता है
                  overflow: 'hidden'
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: isExpanded ? '#FF6A00' : '#94A3B8', fontSize: 28 }} />}
                  sx={{ 
                    p: { xs: 2, md: 2.5 }, 
                    bgcolor: isExpanded ? 'rgba(255,106,0,0.02)' : 'transparent',
                    '& .MuiAccordionSummary-content': { my: 0 }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LightbulbCircle 
                      sx={{ 
                        mr: 2, 
                        color: isExpanded ? '#FF6A00' : '#CBD5E1', 
                        transition: 'color 0.3s' 
                      }} 
                    />
                    <Typography 
                      variant="h6" 
                      fontWeight="700" 
                      color={isExpanded ? '#1E293B' : '#334155'}
                      sx={{ fontSize: { xs: '1.05rem', md: '1.15rem' } }}
                    >
                      {faq.q}
                    </Typography>
                  </Box>
                </AccordionSummary>
                
                <AccordionDetails sx={{ p: { xs: 2.5, md: 3 }, pt: 0, bgcolor: isExpanded ? 'rgba(255,106,0,0.02)' : 'transparent' }}>
                  <Typography variant="body1" color="#475569" lineHeight={1.7}>
                    {faq.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>

        {/* 🌟 CONTACT / CTA SECTION 🌟 */}
        <Box 
          sx={{ 
            background: primaryGradient, borderRadius: 4, p: { xs: 4, md: 5 }, 
            textAlign: 'center', color: 'white', boxShadow: '0 10px 20px rgba(238, 9, 121, 0.2)',
            position: 'relative', overflow: 'hidden'
          }}
        >
          {/* Background circles for styling */}
          <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
          
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <HelpOutline sx={{ fontSize: 40, mb: 1, opacity: 0.9 }} />
            <Typography variant="h4" fontWeight="900" mb={1}>Still Have Questions?</Typography>
            <Typography variant="subtitle1" mb={3} sx={{ opacity: 0.9 }}>
              Can't find the answer you're looking for? Our support team is here to help.
            </Typography>
            
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button 
                component="a" href="mailto:support@pracharapp.com"
                variant="contained" startIcon={<Email />}
                sx={{ 
                  bgcolor: 'white', color: '#EE0979', px: 4, py: 1.2, 
                  borderRadius: '50px', fontWeight: '800', fontSize: '1rem',
                  '&:hover': { bgcolor: '#f8fafc', transform: 'translateY(-2px)' },
                  transition: 'all 0.3s'
                }}
              >
                Contact Support
              </Button>
            </Stack>
          </Box>
        </Box>
      </Container>

    </Box>
  );
}